import { NgComponentOutlet } from '@angular/common';
import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { SharedModule } from 'src/app/shared/shared.module';
import { CommonModule } from '@angular/common';
import { NotificationMasterService } from 'src/app/pages/notification-master/service/notification-master.service';
import { AlertService } from 'src/app/core/service/alert.service';
import { VendorDateTimeService } from 'src/app/core/service/vendor-datetime.service';
import { ContractVendorDatePipe } from 'src/app/pages/contract-master/utils/contract-vendor-date.pipe';
import { ContractVendorDateTimeUtil } from 'src/app/pages/contract-master/utils/contract-vendor-datetime.util';
import { Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { sanitizeHtmlContent } from 'src/app/shared/utils/sanitize-content.util';
import {
    CHAT_DETAIL_META_FIELDS,
    CONTRACT_DETAIL_META_FIELDS,
    EVENT_DETAIL_META_FIELDS,
    PURCHASE_ORDER_DETAIL_META_FIELDS,
    formatAlertTypeTitle,
    genericMetaDisplayRows,
    genericRowsAsStructured,
    hasEventMeta,
    isContractAlertFamily,
    isPurchaseOrderFamily,
    isVendorKycAlertFamily,
    mergeAlertMeta,
    mergeNotificationDetail,
    messageUsesSanitizedHtml,
    notificationBadgeTailwind,
    notificationMessageToPreview,
    tenantInvitationMetaRows,
} from 'src/app/shared/utils/notification-alert.helpers';
export interface Notification {
    id: number;
    title: string;
    alert_type: string;
    message: string;
    meta?: any; // e.g., Requested: Name or Expected by: Date
    meta_data?: any; // Alternative property name
    amount?: string; // optional currency text
    read: boolean;
    iconCmp: any; // Component class to render as icon
    time?: string;
    created_at?: string;
    action?: any;
}
@Component({
    selector: 'app-notification',
    imports: [CommonModule, NgComponentOutlet, SharedModule, ContractVendorDatePipe],
    templateUrl: './notification.component.html',
    styleUrl: './notification.component.css',
})
export class NotificationComponent {
    @Input() notifications: Array<Notification> = [];
    @Input() filteredNotifications: Array<Notification> = [];
    @Output() fetchNotifications = new EventEmitter<void>();
    @Output() closeNotificationPopup = new EventEmitter<void>();
    viewNotificationModel = signal<boolean>(false);
    isReadableNotificationDetails = signal<any>({});
    constructor(
        private readonly notificationMasterService: NotificationMasterService,
        private readonly alertService: AlertService,
        private readonly router: Router,
        private readonly sanitizer: DomSanitizer,
        private readonly vendorDateTimeService: VendorDateTimeService,
        private readonly contractVendorDateTimeUtil: ContractVendorDateTimeUtil,
    ) {}

    readonly mergeAlertMeta = mergeAlertMeta;
    readonly mergeNotificationDetail = mergeNotificationDetail;
    readonly isContractAlertFamily = isContractAlertFamily;
    readonly isPurchaseOrderFamily = isPurchaseOrderFamily;
    readonly notificationBadgeTailwind = notificationBadgeTailwind;
    readonly hasEventMeta = hasEventMeta;
    readonly genericMetaDisplayRows = (
        meta: Record<string, unknown> | null | undefined,
        max = 15,
        alertType?: string,
    ) => genericMetaDisplayRows(meta, max, (value) => this.formatNotificationDate(value, alertType));
    readonly messageUsesSanitizedHtml = messageUsesSanitizedHtml;
    readonly isVendorKycAlertFamily = isVendorKycAlertFamily;
    readonly eventDetailMetaFields = EVENT_DETAIL_META_FIELDS;
    readonly contractDetailMetaFields = CONTRACT_DETAIL_META_FIELDS;
    readonly chatDetailMetaFields = CHAT_DETAIL_META_FIELDS;
    readonly purchaseOrderDetailMetaFields = PURCHASE_ORDER_DETAIL_META_FIELDS;
    readonly tenantInvitationMetaRows = tenantInvitationMetaRows;
    readonly genericRowsAsStructured = genericRowsAsStructured;

    formatTitle(type: string): string {
        return formatAlertTypeTitle(type);
    }

    plainNotificationMessage(message: string | undefined | null): string {
        return notificationMessageToPreview(message ?? '');
    }

    timeAgo(date: string): string {
        if (!date) return '-';
        const parsed = this.vendorDateTimeService.parseDate(date);
        if (!parsed) return '-';
        const time = parsed.getTime();
        const now = new Date().getTime();
        const diff = Math.floor((now - time) / 1000);

        if (diff < 60) return 'Just now';
        if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
        return `${Math.floor(diff / 86400)} day(s) ago`;
    }

    handleNotificationClick(notification: Notification) {
        this.fetchByIdNotification(notification.id);
    }

    fetchByIdNotification(id: number) {
        this.viewNotificationModel.set(true);
        this.notificationMasterService.fetchByIdNotification(id).subscribe({
            next: (res) => {
                if (res.status) {
                    this.filteredNotifications = this.filteredNotifications.map((notification) => {
                        if (notification.id === id) {
                            notification.read = res?.data?.is_read;
                        }
                        return notification;
                    });

                    this.isReadableNotificationDetails.set(res.data);
                    this.fetchNotifications.emit();
                }
            },
            error: (error) => {
                this.alertService.showAlert('Error', error?.error?.message || error?.error?.error, 'error');
            },
        });
    }

    closeViewNotificationModal() {
        this.viewNotificationModel.set(false);
    }

    getTimeAgo(dateString: string): string {
        if (!dateString) return '';
        const date = this.vendorDateTimeService.parseDate(dateString);
        if (!date) return '';
        const now = new Date();
        const diffInMs = now.getTime() - date.getTime();
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInDays = Math.floor(diffInHours / 24);

        if (diffInHours < 1) {
            const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
            return `${diffInMinutes} min ago`;
        } else if (diffInHours < 24) {
            return `${diffInHours} hr ago`;
        } else {
            return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
        }
    }

    formatDate(date: string) {
        return this.vendorDateTimeService.format(date, { fallback: '' });
    }

    /**
     * @description Contract alerts use contract-master date-only timezone format.
     */
    formatNotificationDate(
        value: string | number | Date | null | undefined,
        alertType?: string,
    ): string {
        if (isContractAlertFamily(alertType)) {
            return this.contractVendorDateTimeUtil.format(value, '');
        }
        return this.vendorDateTimeService.format(value, { fallback: '' });
    }

    navigateToNotificationList() {
        this.closeNotificationPopup.emit();
        this.router.navigate(['/notification-master/notification']);
    }

    isTableMessage(message: string): boolean {
        return message.includes('<table');
    }
    sanitizeHtml(html: string): string {
        return sanitizeHtmlContent(this.sanitizer, html);
    }
}
